import Cookies from 'js-cookie';
import { useContext } from 'react';

import { ACCESS_TOKEN_COOKIE_NAME, MD_AUTH_LOCAL_STORAGE_KEY } from '~/common/constants/auth';
import { RestApi } from '~/services/restApi';
import { setLocalStorage } from '~/utils/localStorage';

import { AuthenticationContext } from './AuthenticationProvider';

import type { Context } from './AuthenticationProvider';
import type { GetAuthTokenRESTResponse } from '~/services/auth/types';

type UseAuthContext = {
  checkLoginAndOpenAuthDialog: (
    callBack: () => void,
    title: string,
    successRedirectUrl?: string
  ) => void;
  checkLoginAndOpenLoginRequiredDialog: (callBack: () => void, title: string) => void;
  handleUserLoggedIn: (auth: GetAuthTokenRESTResponse) => void;
  openLoginRequiredDialog: (title: string) => void;
} & Context;

export const useAuthenticationContext = (): UseAuthContext => {
  const authContext = useContext(AuthenticationContext);
  const {
    isUserLoggedIn,
    openLoginRequiredDialog,
    openAuthDialog,
    setIsUserLoggedIn,
  } = authContext;

  const checkLoginAndOpenAuthDialog = (
    callBack: () => void,
    title: string,
    successRedirectUrl?: string
  ): void => {
    if (!isUserLoggedIn) {
      openAuthDialog(title, successRedirectUrl);
      return;
    }

    callBack();
  };

  const checkLoginAndOpenLoginRequiredDialog = (callBack: () => void, title: string): void => {
    if (!isUserLoggedIn) {
      openLoginRequiredDialog(title);
      return;
    }

    callBack();
  };

  const handleUserLoggedIn = (auth: GetAuthTokenRESTResponse): void => {
    Cookies.set(ACCESS_TOKEN_COOKIE_NAME, JSON.stringify({ tokens: auth }) ?? '');
    setLocalStorage(MD_AUTH_LOCAL_STORAGE_KEY, { ...auth });
    RestApi.setAuthorizationHeader({
      authorization: `Bearer ${auth.access_token}`,
    });
    setIsUserLoggedIn(true);
  };

  return {
    checkLoginAndOpenAuthDialog,
    checkLoginAndOpenLoginRequiredDialog,
    handleUserLoggedIn,
    ...authContext,
  };
};
